import { POSITIONING_VARIANT_STORAGE_KEY, type PositioningVariant } from '@/constants/funnel';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { useFunnelLead } from '@/hooks/useFunnelLead';
import {
  formatIsoDate,
  parseMmDdYyyy,
  validateAge,
  type AgeValidationStatus,
} from '@/utils/funnel/age-validation';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JoinFunnelLayout } from '../JoinFunnelLayout';
import { FunnelField, FunnelFieldGroup } from '../components/FunnelField';
import { ValuePropCard } from '../components/ValuePropCard';
import { FUNNEL_COPY } from '../funnel-copy';
import { stepRoutePath } from '../routes';

type BiologicalSex = 'female' | 'male';

function readVariant(): PositioningVariant {
  const stored = sessionStorage.getItem(POSITIONING_VARIANT_STORAGE_KEY);
  if (stored === 'test-before-test' || stored === 'checkup' || stored === 'default') {
    return stored;
  }
  return 'default';
}

function maskDob(input: string): string {
  // Strip non-digits, then re-insert slashes at MM/DD positions.
  const digits = input.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function ageWarningCopy(
  status: AgeValidationStatus,
  isFutureDate: boolean,
): { title: string; body: string } | null {
  if (status === 'invalid' && isFutureDate) {
    return {
      title: "That date is in the future.",
      body: 'Please enter your real date of birth.',
    };
  }
  if (status === 'under_min') {
    return {
      title: 'You must be 18 or older to use Catch.',
      body: 'Catch is currently only available to adults aged 18 and over.',
    };
  }
  if (status === 'over_soft_threshold') {
    return {
      title: "Catch isn't well-suited for your age group.",
      body:
        'Catch is designed for adults under 85. Our risk models may not apply well at your age.',
    };
  }
  return null;
}

interface AgeWarningProps {
  status: AgeValidationStatus;
  isFutureDate: boolean;
}

function AgeWarning({ status, isFutureDate }: AgeWarningProps) {
  const copy = ageWarningCopy(status, isFutureDate);
  if (!copy) return null;
  return (
    <div className="flex items-start gap-3 rounded-2xl border-1 border-solid border-amber-300 bg-amber-50 px-4 py-3">
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mt-0.5 flex-shrink-0 text-amber-600"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <div className="flex flex-col gap-1">
        <span className="text-sm font-bold text-catch-dark-blue-90">{copy.title}</span>
        <span className="text-sm text-gray-700">{copy.body}</span>
      </div>
    </div>
  );
}

export function JoinStep2() {
  const variant = readVariant();
  const copy = FUNNEL_COPY[variant].step2;
  const navigate = useNavigate();
  const { lead, updateLead } = useFunnelLead();
  const analytics = useAnalyticsCalls();

  const [dob, setDob] = useState('');
  const [sex, setSex] = useState<BiologicalSex | null>(null);

  // Hydrate from server.
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (!lead || hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    if (lead.dateOfBirth) {
      const [y, m, d] = lead.dateOfBirth.split('-');
      setDob(`${m}/${d}/${y}`);
    }
    if (lead.biologicalSex === 'female' || lead.biologicalSex === 'male') {
      setSex(lead.biologicalSex);
    }
  }, [lead]);

  const hasFiredViewRef = useRef(false);
  useEffect(() => {
    if (hasFiredViewRef.current) return;
    hasFiredViewRef.current = true;
    analytics.callFunnelStepViewed(2, variant);
  }, [analytics, variant]);

  const dobDate = parseMmDdYyyy(dob);
  const ageResult = dobDate ? validateAge(dobDate) : { status: 'invalid' as const, age: null };
  const isFutureDate = dobDate !== null && dobDate.getTime() > Date.now();

  // Fire age-error analytics when the warning becomes visible (transitions
  // from non-error to error). Avoids spamming on every keystroke.
  const lastFiredStatusRef = useRef<AgeValidationStatus | null>(null);
  useEffect(() => {
    if (ageResult.status !== 'under_min' && ageResult.status !== 'over_soft_threshold') {
      lastFiredStatusRef.current = ageResult.status;
      return;
    }
    if (lastFiredStatusRef.current === ageResult.status) return;
    lastFiredStatusRef.current = ageResult.status;
    if (ageResult.age != null) {
      analytics.callFunnelAgeErrorShown({
        errorType: ageResult.status === 'under_min' ? 'under_18' : 'over_85',
        age: ageResult.age,
        positioningVariant: variant,
      });
    }
  }, [ageResult.status, ageResult.age, analytics, variant]);

  const handleDobBlur = async () => {
    if (!dobDate || ageResult.status === 'under_min' || ageResult.status === 'invalid') {
      // Don't persist invalid or blocked DOBs — per spec, under-18 is a
      // client-side block; we choose not to phone home a value we won't ship.
      return;
    }
    try {
      await updateLead({ dateOfBirth: formatIsoDate(dobDate) });
      analytics.callFunnelFieldCaptured({
        field: 'date_of_birth',
        value: formatIsoDate(dobDate),
        age: ageResult.age ?? undefined,
        positioningVariant: variant,
      });
    } catch {
      // Silently swallow — the user will see the failure on Continue if it
      // matters; PATCH retries are cheap.
    }
  };

  const handleSelectSex = async (next: BiologicalSex) => {
    setSex(next);
    try {
      await updateLead({ biologicalSex: next });
      analytics.callFunnelFieldCaptured({
        field: 'biological_sex',
        value: next,
        positioningVariant: variant,
      });
    } catch {
      // Same as DOB — surface only at Continue if it matters.
    }
  };

  const isComplete =
    sex !== null &&
    ageResult.status !== 'invalid' &&
    ageResult.status !== 'under_min';

  const handleContinue = () => {
    if (!isComplete) return;
    navigate(stepRoutePath(variant, 3));
  };

  const handleBack = () => {
    navigate(stepRoutePath(variant, 1));
  };

  return (
    <JoinFunnelLayout
      step={2}
      onBack={handleBack}
      onContinue={handleContinue}
      isContinueDisabled={!isComplete}
    >
      <div className="flex flex-col gap-3">
        <h1 className="text-3xl font-bold leading-tight text-catch-dark-blue-90">
          {copy.header}
        </h1>
        <p className="text-base text-gray-500">{copy.subheader}</p>
      </div>

      <div className="flex flex-col gap-4">
        <FunnelField
          id="dob"
          label="Date of birth"
          placeholder="MM / DD / YYYY"
          value={dob}
          onChange={(e) => setDob(maskDob(e.target.value))}
          onBlur={handleDobBlur}
          inputMode="numeric"
          autoComplete="bday"
        />
        <AgeWarning status={ageResult.status} isFutureDate={isFutureDate} />

        <FunnelFieldGroup label="Biological sex">
          <div className="grid grid-cols-2 gap-3">
            {(['female', 'male'] as const).map((option) => {
              const isActive = sex === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleSelectSex(option)}
                  className={`h-14 rounded-2xl border-solid text-base font-semibold text-catch-dark-blue-90 transition-colors ${
                    isActive
                      ? 'border-2 border-catch-light-blue bg-catch-light-blue/25'
                      : 'border-1 border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  {option === 'female' ? 'Female' : 'Male'}
                </button>
              );
            })}
          </div>
        </FunnelFieldGroup>
      </div>

      <ValuePropCard
        copy={copy.valueProp}
        trailing={
          <figure className="flex flex-col gap-3">
            <span className="block text-3xl leading-none text-gray-300" aria-hidden="true">
              &ldquo;
            </span>
            <blockquote className="text-base italic leading-relaxed text-gray-700">
              {copy.quote.text}
            </blockquote>
            <figcaption className="text-sm text-gray-500">{copy.quote.attribution}</figcaption>
          </figure>
        }
      />
    </JoinFunnelLayout>
  );
}

export default JoinStep2;
